import { ReactElement, useState } from 'react';
import { CompanyUser } from '@/api';
import { Link } from 'react-router';
import {
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    UISectionState,
    UIText,
    useUIConfirmationModal
} from '@/ui';
import { useCompanyContext } from '../providers';
import { useFetchUserCounts } from '../queries';
import { useCompaniesTranslation, useCompanyPermissions } from '../hooks';
import {
    useAssignLicenseAction,
    useRemoveLicenseAction,
    useUpdateCompanyRoleAction
} from '../actions';
import { UpdateCompanyRoleModal } from './update-company-role-modal';

type AccessSettingsSectionProps = {
    companyUser: CompanyUser;
};

/**
 * A user's company role and license. Both are company-wide entitlements, and
 * each row carries its own action button — the section header is left bare
 * because a shared header couldn't say which button applied to which row.
 */
export function MemberAccessSettingsSection(
    props: AccessSettingsSectionProps
): ReactElement {
    const { companyUser } = props;

    const { t } = useCompaniesTranslation();

    const { company } = useCompanyContext();
    // Company-wide counts. Both actions below are capped by them, so the
    // section holds off rather than showing controls that don't know the caps.
    const {
        counts,
        status: countsStatus,
        refetch
    } = useFetchUserCounts(company.id);
    const { canViewBilling } = useCompanyPermissions();

    const confirmationModal = useUIConfirmationModal();

    const [isRoleEditOpen, setIsRoleEditOpen] = useState(false);

    // Both rows always show their button. Each action carries its own reason,
    // so a viewer who may not use one is told why rather than being shown a row
    // with no controls at all.
    const updateCompanyRoleAction = useUpdateCompanyRoleAction({
        user: companyUser,
        ownerCount: counts?.joinedOwner ?? 0
    });

    // A license is assigned or removed outright — there is nothing to edit — so
    // the two actions are mutually exclusive and each carries its own reason for
    // being unavailable (no subscription, no seats left, already assigned...).
    const assignAction = useAssignLicenseAction({
        user: companyUser,
        assignedLicenses: counts?.assignedLicense ?? 0
    });
    const removeAction = useRemoveLicenseAction({ user: companyUser });

    const license = companyUser.license;
    const subscription = company.subscription;

    // A trial isn't capped by the subscription quantity, so don't imply a limit.
    const showSeatCount =
        subscription !== null && subscription.status !== 'trialing';

    function onRemoveLicense() {
        confirmationModal.open({
            title: t(
                'components.member_access_settings_section.license.remove_confirm_title'
            ),
            confirmText: t(
                'components.member_access_settings_section.license.remove_confirm_button'
            ),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                removeAction
                    .removeLicense()
                    .catch(() => {}) // Error handled in hook
                    .finally(() => {
                        confirmationModal.setLoading(false);
                        confirmationModal.close();
                    });
            },
            onCancel: () => {
                confirmationModal.close();
            }
        });
    }

    if (!counts) {
        return (
            <UISectionState
                title={t(
                    'components.member_access_settings_section.access.title'
                )}
                status={countsStatus}
                errorText={t(
                    'components.member_access_settings_section.access.load_error'
                )}
                onRetry={refetch}
            />
        );
    }

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={t(
                        'components.member_access_settings_section.access.title'
                    )}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <Row
                            label={t(
                                'components.member_access_settings_section.company_role.title'
                            )}
                            description={t(
                                `shared.roles.company.${companyUser.role}.description`
                            )}
                            action={
                                <UIButton
                                    text={t('shared.labels.edit')}
                                    variant="textDefault"
                                    type="button"
                                    disabled={
                                        !updateCompanyRoleAction.isAllowed
                                    }
                                    disabledTooltip={
                                        updateCompanyRoleAction.reason
                                    }
                                    onClick={() => setIsRoleEditOpen(true)}
                                />
                            }
                        >
                            <UIText medium>
                                {t(
                                    `shared.roles.company.${companyUser.role}.name`
                                )}
                            </UIText>
                        </Row>

                        {/* Unlike the role row, the licence buttons are always
                            rendered — the action carries its own reason, so a
                            viewer who may not manage this member's licence is
                            told why rather than being shown a row with no
                            controls at all. */}
                        <Row
                            label={t(
                                'components.member_access_settings_section.license.title'
                            )}
                            description={t(
                                'components.member_access_settings_section.license.status_description'
                            )}
                            action={
                                license ? (
                                    <UIButton
                                        text={t(
                                            'components.member_access_settings_section.license.remove_button'
                                        )}
                                        variant="textDanger"
                                        type="button"
                                        loading={removeAction.loading}
                                        disabled={!removeAction.isAllowed}
                                        disabledTooltip={removeAction.reason}
                                        onClick={onRemoveLicense}
                                    />
                                ) : (
                                    <UIButton
                                        text={t(
                                            'components.member_access_settings_section.license.assign_button'
                                        )}
                                        variant="textPrimary"
                                        type="button"
                                        loading={assignAction.loading}
                                        disabled={!assignAction.isAllowed}
                                        disabledTooltip={assignAction.reason}
                                        onClick={() => {
                                            assignAction
                                                .assignLicense()
                                                .catch(() => {}); // Error handled in hook
                                        }}
                                    />
                                )
                            }
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText
                                    medium
                                    type={license ? 'link' : 'error'}
                                >
                                    {license
                                        ? t(
                                              'components.member_access_settings_section.license.assigned'
                                          )
                                        : t(
                                              'components.member_access_settings_section.license.not_assigned'
                                          )}
                                </UIText>
                                {showSeatCount && (
                                    <UIText variant="small" type="default2">
                                        {t(
                                            'components.member_access_settings_section.license.seats_used',
                                            {
                                                used: counts.assignedLicense,
                                                total: subscription.quantity
                                            }
                                        )}
                                    </UIText>
                                )}
                            </div>
                        </Row>

                        {/* Without a subscription there are no licenses to give
                            out at all, so point at Billing rather than leaving
                            the disabled Assign button as the only explanation. */}
                        {!subscription && canViewBilling && (
                            <Row
                                label={t(
                                    'components.member_access_settings_section.license.no_subscription'
                                )}
                                description={t(
                                    'components.member_access_settings_section.license.no_subscription_description'
                                )}
                            >
                                <Link
                                    to={`/company/${company.id}/settings/billing`}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <UIText type="link" medium>
                                        {t(
                                            'components.member_access_settings_section.license.view_billing'
                                        )}
                                    </UIText>
                                </Link>
                            </Row>
                        )}
                    </div>
                </UISectionContent>
            </UISection>

            <UpdateCompanyRoleModal
                companyUser={isRoleEditOpen ? companyUser : null}
                ownerCount={counts.joinedOwner}
                onClose={() => setIsRoleEditOpen(false)}
            />
        </>
    );
}

function Row(props: {
    label: string;
    description: string;
    /** Rendered at the end of the row, e.g. an Edit or Assign button. */
    action?: ReactElement;
    children: ReactElement;
}): ReactElement {
    const { label, description, action, children } = props;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4
                }}
            >
                <UIText>{label}</UIText>
                <UIText type="default2">{description}</UIText>
            </div>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16
                }}
            >
                {children}
                {action}
            </div>
        </div>
    );
}
